import banner from "../../../courses/module-1/img1.png";

const modulo1 = {
  courseId: "finanzas-personales",
  id: "modulo-1",
  title: "📘 MÓDULO 1: Introducción a las Finanzas Personales",
  estimatedDuration: "1 hora",
  format: "Lectura + actividad reflexiva + evaluación",
  bannerUrl: banner,
  sections: [
    {
      id: "objetivos",
      navLabel: "Objetivos",
      title: "🎯 Objetivos del módulo",
      content: [
        "Entender qué son las finanzas personales y por qué son clave en la vida adulta.",
        "Cuestionar mitos culturales que limitan nuestra relación con el dinero.",
        "Inspirarte para empezar a gestionar tu dinero desde ahora, con herramientas reales y ejemplos cercanos.",
      ],
    },
    {
      id: "lectura",
      navLabel: "Lectura principal",
      title: "📖 Lectura principal: ¿Qué son las finanzas personales?",
      content: [
        {
          type: "paragraph",
          text: "Las finanzas personales son todas las decisiones y acciones que tomas con respecto a tu dinero. No se trata de grandes cifras ni de ser “bueno para los números”. Es simplemente responder preguntas cotidianas como:",
        },
        {
          type: "list",
          items: [
            "¿En qué me gasto la quincena?",
            "¿Cuánto necesito para cubrir mis gastos fijos?",
            "¿Cómo puedo empezar a ahorrar para algo importante?",
            "¿Me alcanza para ir al viaje de graduación?",
            "¿Cómo hago para no endeudarme con la tarjeta?",
          ],
        },
        {
          type: "paragraph",
          text: "Ejemplo cercano: María, de 18 años, vive en Guadalajara y estudia en la UDG. Recibe una beca de manutención y trabaja medio turno en un café. Empezó anotando todos sus gastos diarios en una app gratuita. Descubrió que gastaba $350 pesos al mes en snacks. Cambió ese hábito y ahora ahorra esa misma cantidad para comprarse un celular nuevo.",
        },
      ],
    },
    {
      id: "importancia",
      navLabel: "Importancia",
      title: "🧠 ¿Por qué es tan importante aprender esto?",
      content: [
        {
          type: "paragraph",
          text: "Porque si tú no decides cómo usar tu dinero, alguien más lo hará (la publicidad, las redes, el algoritmo, el antojo del momento).",
        },
        {
          type: "paragraph",
          text: "Manejar bien tus finanzas no es solo una habilidad útil, es una herramienta de libertad. Te ayuda a:",
        },
        {
          type: "list",
          items: [
            "Evitar estrés financiero",
            "Decidir sin miedo",
            "Ahorrar para lo que realmente quieres",
            "Vivir con más claridad y autonomía",
          ],
        },
      ],
    },
    {
      id: "mitos",
      navLabel: "Mitos comunes",
      title: "🧨 Mitos comunes sobre el dinero",
      content: [
        {
          type: "orderedList",
          items: [
            `"Estoy muy joven para pensar en eso": Al contrario: cuanto antes empieces, más ventaja tienes. Si empiezas a los 17 con el hábito del ahorro, a los 25 ya tendrás una base fuerte.`,
            `"No me alcanza para ahorrar": No se trata de cuánto, sino de empezar. $10 a la semana → $520 al año. Si logras ese hábito, ya estás construyendo tu mentalidad financiera.`,
            `"Las finanzas son solo para gente rica o experta": No necesitas saber de bolsa o criptomonedas para tomar buenas decisiones. Entender lo básico (ingresos, gastos, ahorro, crédito) es más que suficiente para empezar.`,
          ],
        },
      ],
    },
    {
      id: "actividad",
      navLabel: "Actividad reflexiva",
      title: "💡 Actividad reflexiva",
      content: [
        "Tómate un momento para contestar (escribiendo en papel o en tu celular):",
        {
          type: "list",
          items: [
            "¿Qué piensas cuando escuchas la palabra dinero?",
            "¿Qué frase sobre el dinero escuchaste mucho en casa?",
            "¿Alguna vez sentiste ansiedad por no saber si te iba a alcanzar? ¿Qué hiciste?",
            "¿Qué te gustaría cambiar en tu relación con el dinero a partir de hoy?",
          ],
        },
      ],
    },
    {
      id: "recurso",
      navLabel: "Recurso adicional",
      title: "🎬 Recurso adicional",
      content: [
        "Para reforzar lo aprendido en este módulo, te invitamos a ver el siguiente video que ofrece una guía paso a paso sobre finanzas personales para principiantes. Este recurso te ayudará a visualizar y comprender mejor los conceptos clave que hemos abordado.",
        {
          type: "link",
          href: "https://www.youtube.com/watch?v=hddYOF8nx24",
          text: "La educación financiera básica: ¿Qué debes saber antes de empezar?",
        },
      ],
    },
    {
      id: "lecturas",
      navLabel: "Lecturas recomendadas",
      title: "📚 Lecturas recomendadas",
      content: [
        {
          type: "link",
          href: "http://www.condusef.gob.mx/",
          text: "Finanzas para Jóvenes – CONDUSEF",
        },
        {
          type: "link",
          href: "https://www.pequenocerdocapitalista.com/",
          text: "Blog Pequeño Cerdo Capitalista",
        },
      ],
    },
    {
      id: "quiz",
      navLabel: "Quiz",
      title: "📝 Miniquiz de autoevaluación",
      questions: [
        {
          id: "q1",
          question: "¿Qué abarca el término *finanzas personales*?",
          options: [
            { key: "a", text: "Invertir en la bolsa" },
            { key: "b", text: "Solo mis ingresos" },
            {
              key: "c",
              text: "Todo lo que hago con mi dinero: ingresos, gastos, ahorro, etc.",
            },
            { key: "d", text: "Hablar de dinero con un banco" },
          ],
          correct: "c",
        },
        {
          id: "q2",
          question: "¿Cuál es un mito común?",
          options: [
            { key: "a", text: "Que ahorrar es clave" },
            { key: "b", text: "Que el dinero no es para jóvenes" },
            { key: "c", text: "Que los gastos deben controlarse" },
            { key: "d", text: "Que existen seguros" },
          ],
          correct: "b",
        },
        {
          id: "q3",
          question: "¿Qué te puede ayudar a vivir con más tranquilidad?",
          options: [
            { key: "a", text: "Comprar sin pensar" },
            { key: "b", text: "Usar todo tu crédito" },
            { key: "c", text: "Manejar tus finanzas personales" },
            { key: "d", text: "Ignorar tus deudas" },
          ],
          correct: "c",
        },
        {
          id: "q4",
          question: "¿Qué se recomienda aunque ganes poco?",
          options: [
            { key: "a", text: "Invertir en cripto" },
            { key: "b", text: "Ahorrar aunque sea $10" },
            { key: "c", text: "No hablar del tema" },
            { key: "d", text: "Vivir al día" },
          ],
          correct: "b",
        },
        {
          id: "q5",
          question: "¿Por qué es importante iniciar joven?",
          options: [
            { key: "a", text: "Porque te obligan" },
            { key: "b", text: "Porque es moda" },
            { key: "c", text: "Porque el tiempo multiplica tu dinero" },
            { key: "d", text: "Porque todos lo hacen" },
          ],
          correct: "c",
        },
      ],
    },
    {
      id: "bibliografia",
      navLabel: "Bibliografía",
      title: "📚 Bibliografía para profundizar",
      content: [
        "Sofía Macías – “Pequeño Cerdo Capitalista”",
        "Ramsey Solutions – “La transformación total de su dinero”",
        "Luis Pita – “Ten peor coche que tu vecino”",
      ],
    },
    {
      id: "frase",
      navLabel: "Frase del día",
      title: "🧠 Frase del día",
      content: ["“El dinero no te define, pero saber usarlo te da libertad.”"],
    },
  ],
};

export default modulo1;
